// Campaigns list — the new home for drip-builder workflow.
//
// Each row is a saved campaign (e.g. "Opening 10"). Click into a row to review,
// edit, approve, send. "New campaign" launches the wizard.

import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import EmailMovedBanner from "@/components/email/email-moved-banner";
import { Plus, RefreshCw, Mail, CheckCircle2, Send, Archive, Clock, AlertTriangle, Compass, ArrowRight, Trash2 } from "lucide-react";

const ADMIN = () => localStorage.getItem("admin_token") || "";

async function api<T = any>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", "x-admin-token": ADMIN(), ...(init?.headers || {}) },
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || `HTTP ${res.status}`);
  }
  return res.json();
}

interface Campaign {
  id: number;
  name: string;
  track: string;
  status: "draft" | "approved" | "sending" | "done" | "archived";
  brief: any;
  createdAt: string;
  approvedAt: string | null;
  lastSendStartedAt: string | null;
}

const STATUS_STYLE: Record<string, { label: string; class: string; icon: any }> = {
  draft:     { label: "Draft",     class: "bg-amber-500/10 text-amber-700 border-amber-500/30", icon: Clock },
  approved:  { label: "Approved",  class: "bg-blue-500/10 text-blue-700 border-blue-500/30",   icon: CheckCircle2 },
  sending:   { label: "Sending",   class: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30", icon: Send },
  done:      { label: "Done",      class: "bg-muted text-muted-foreground border-border",       icon: CheckCircle2 },
  archived:  { label: "Archived",  class: "bg-muted text-muted-foreground border-border",       icon: Archive },
};

export default function AdminCampaigns() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const reload = async () => {
    setLoading(true); setError(null);
    try {
      const j = await api<{ campaigns: Campaign[] }>("/campaigns");
      setCampaigns(j.campaigns || []);
    } catch (err: any) {
      setError(err?.message || "Failed to load");
    }
    setLoading(false);
  };

  useEffect(() => { reload(); }, []);

  const handleDelete = async (c: Campaign) => {
    const ok = window.confirm(
      `Delete campaign "${c.name}"?\n\nThis removes the campaign and its 10 emails permanently. ` +
      `Any past recipients will be detached but their send history is preserved.\n\nThis cannot be undone.`
    );
    if (!ok) return;
    setDeletingId(c.id); setError(null);
    try {
      await api(`/campaigns/${c.id}`, { method: "DELETE" });
      setCampaigns((prev) => prev.filter((x) => x.id !== c.id));
    } catch (err: any) {
      setError(err?.message || "Delete failed");
    }
    setDeletingId(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-serif tracking-tight">Drip campaigns</h1>
          <p className="text-sm text-muted-foreground mt-1">Build, review, approve, and send 10-email onboarding drips. Each campaign is its own bundle of brief + emails + recipients.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={reload}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-md border border-border hover:bg-muted transition disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <Link href="/admin/campaigns/new">
            <a className="inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-md bg-[#FF5C00] text-white hover:bg-[#FF5C00]/90">
              <Plus className="w-3.5 h-3.5" /> Build a campaign
            </a>
          </Link>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-md text-sm border bg-red-500/10 border-red-500/30 text-red-700">{error}</div>
      )}

      <EmailMovedBanner />

      <Link href="/admin/campaigns/roadmap">
        <a className="block rounded-lg border border-[#FF5C00]/30 bg-gradient-to-r from-[#FF5C00]/10 via-[#FF5C00]/5 to-transparent p-4 hover:border-[#FF5C00]/60 transition-colors group">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-9 h-9 rounded-md bg-[#FF5C00]/15 text-[#FF5C00] flex items-center justify-center shrink-0">
                <Compass className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold flex items-center gap-2 flex-wrap">
                  V2 Roadmap — where this is going next
                  <span className="text-[10px] font-normal px-1.5 py-0.5 rounded border border-[#FF5C00]/40 bg-background text-[#FF5C00] uppercase tracking-wide">PRD</span>
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  AI brief parser, variable email count, absolute-date pacing, calendar invites, reply tracking. Read the future of drip campaigns.
                </p>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-[#FF5C00] shrink-0 group-hover:translate-x-1 transition-transform" />
          </div>
        </a>
      </Link>

      {campaigns.length === 0 && !loading ? (
        <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">
          <Mail className="w-5 h-5 mx-auto mb-2" />
          No campaigns yet. Click "Build a campaign" to create your first.
        </div>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border overflow-hidden">
          {campaigns.map((c) => {
            const s = STATUS_STYLE[c.status] || STATUS_STYLE.draft;
            const Icon = s.icon;
            return (
              <li key={c.id} className="relative group">
                <Link href={`/admin/campaigns/${c.id}`}>
                  <a className="block px-4 py-3 pr-14 hover:bg-muted/40 transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium truncate">{c.name}</p>
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded border ${s.class}`}>
                            <Icon className="w-3 h-3" /> {s.label}
                          </span>
                          <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] rounded bg-muted text-muted-foreground border border-border">
                            {c.track === "investor" ? "Investor" : "Advisor"}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Created {new Date(c.createdAt).toLocaleDateString()}
                          {c.approvedAt ? ` · approved ${new Date(c.approvedAt).toLocaleDateString()}` : ""}
                          {c.lastSendStartedAt ? ` · last send ${new Date(c.lastSendStartedAt).toLocaleDateString()}` : ""}
                        </p>
                      </div>
                    </div>
                  </a>
                </Link>
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDelete(c); }}
                  disabled={deletingId === c.id || c.status === "sending"}
                  title={c.status === "sending" ? "Cannot delete a campaign that is currently sending" : "Delete campaign"}
                  aria-label={`Delete campaign ${c.name}`}
                  className="absolute top-1/2 right-3 -translate-y-1/2 p-2 rounded-md text-muted-foreground hover:text-red-600 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 focus:opacity-100 transition disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
                >
                  <Trash2 className={`w-4 h-4 ${deletingId === c.id ? "animate-pulse" : ""}`} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
